import {
  Avatar,
  Box,
  Grid,
  Tab,
  Tabs,
  Tooltip,
  useMediaQuery,
} from "@mui/material";

import ArticleIcon from "@mui/icons-material/Article";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import CreateIcon from "@mui/icons-material/Create";
import GroupIcon from "@mui/icons-material/Group";
import ImportContactsIcon from "@mui/icons-material/ImportContacts";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { Icons } from "assets";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Card from "shared/components/card";
import ItemsPerPage from "shared/components/itemsPerPage";
import Pagination from "shared/components/pagination";
import { toastMessage } from "shared/components/toast";
import useBreadcrumbs from "shared/customHooks/useBreadCrumbs";
import useDebounce from "shared/customHooks/useDebounce";
import Confirmation from "shared/modals/confirmation";
import { routeConstant } from "shared/routes/routesConstant";
import {
  getDashboardData,
  getUsersServices,
} from "shared/services/adminService";
import SkeletonLoader from "./skeletonLoader";
import styles from "./style.module.scss";
import DemoRequests from "pages/demoRequests";

const tab_items = [
  {
    label: "Partner Accounts",
    value: 0,
    slug: "partner-accounts",
  },
  {
    label: "Demo Requests",
    value: 1,
    slug: "demo-requests",
  },
];

function Dashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromParam = tab_items.find(
    (item) => item.slug === searchParams.get("tab")
  );
  const [activeTab, setActiveTab] = useState<any>(
    tabFromParam ? tabFromParam.value : tab_items[0].value
  );
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [dashboardData, setDashboardData] = useState<any>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [activePage, setActivePage] = useState<number>(1);
  const [users, setAllUsers] = useState<any>([]);
  // Starts true: a fetch is fired on mount, so the first paint must be the
  // skeleton. Starting false flashes "No Data Found" before the request runs.
  const [loader, setLoader] = useState<boolean>(true);
  const [totalPage, setTotalPage] = useState<number>(0);
  const [search, setSearch] = useState<string>("");
  const [searchText, setSearchText] = useState<string>("");
  // eslint-disable-next-line
  const [selectedFilter, setSelectedFilter] = useState<any>(null);
  const [totalItems, setTotalItems] = useState<number>(1);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);

  const handleOpenDelete = () => {
    setOpenDelete(true);
  };

  const partnersCardData = [
    {
      title: "Total Publishers",
      icon: GroupIcon,
      stats: dashboardData?.publisher?.total,
    },
    {
      title: "Total Authors",
      icon: CreateIcon,
      stats: dashboardData?.author?.total,
    },
    {
      title: "Total Book Reviewers",
      icon: ImportContactsIcon,
      stats: dashboardData?.book_reviewer?.total,
    },
  ];

  const handleClick = () => {
    setOpenDelete(false);
  };
  // GET PARTNER ACCOUNTS USERS
  const getAllUsers = () => {
    setLoader(true);
    getUsersServices(searchText, 0, "", activePage, itemsPerPage)
      .then(({ data }) => {
        setLoader(false);
        setTotalPage(data?.data?.last_page);
        setAllUsers(data?.data?.data);
        setTotalItems(data?.data?.total);
        setItemsPerPage(data?.data?.per_page);
        setCurrentPage(data?.data?.current_page);
      })
      .catch((err) => {
        toastMessage("error", err?.response?.data?.message);
        console.log("err", err?.response?.data?.message);
        setLoader(false);
      });
  };

  const getDashboardStats = () => {
    setIsLoading(true);
    getDashboardData()
      .then(({ data }) => {
        setIsLoading(false);
        setDashboardData(data?.data);
      })
      .catch((err) => {
        setIsLoading(false);
        toastMessage("error", err?.response?.data?.message);
        console.log("err", err?.response?.data?.message);
      });
  };

  useEffect(() => {
    if (activeTab === 0) {
      getAllUsers();
    }

    // eslint-disable-next-line
  }, [searchText, selectedFilter, activePage, itemsPerPage, activeTab]);

  useEffect(() => {
    if (activeTab === 0) {
      getDashboardStats();
    }
  }, [activeTab]);

  useEffect(() => {
    const currentSlug = searchParams.get("tab");
    const matched = tab_items.find((item) => item.slug === currentSlug);
    if (!matched) {
      const fallback = tab_items.find((item) => item.value === activeTab);
      if (fallback) {
        setSearchParams({ tab: fallback.slug }, { replace: true });
      }
      return;
    }
    if (matched.value !== activeTab) {
      // Same reason as the tab click: back/forward changes the tab here, and
      // the refetch only runs afterwards in an effect.
      setLoader(true);
      setActiveTab(matched.value);
      setActivePage(1);
    }
    // eslint-disable-next-line
  }, [searchParams]);

  useDebounce(
    () => {
      const trimmedSearch = search.trim();
      // Nothing changed once trimmed (e.g. only spaces were typed) — keep the
      // current page and skip the request entirely.
      if (trimmedSearch === searchText) return;
      setSearchText(trimmedSearch);
      setActivePage(1);
    },
    [search],
    800
  );

  const admin_roles = [
    { id: 1, label: "School" },
    { id: 2, label: "Teacher" },
    { id: 3, label: "Parent" },
    { id: 4, label: "Reader" },
    { id: 5, label: "Author" },
    { id: 6, label: "Publisher" },
  ];

  const MOU_Status = [
    { label: "Draft", value: 0 },
    { label: "Review", value: 1 },
    { label: "Pending", value: 2 },
    { label: "Accepted", value: 3 },
    { label: "Declined", value: 4 },
    { label: "Agreement Signed", value: 6 },
    { label: "Agreement Signed", value: 7 },
  ];

  useBreadcrumbs({
    title: "Dashboard",
    path: routeConstant?.dashboard?.path,
  });
  const cards = activeTab === 0 ? partnersCardData : null;
  return (
    <div>
      {cards && (
        <Grid container spacing={2}>
          {cards.map((item, index) => {
            return (
              <React.Fragment key={index}>
                <Grid item xs={12} md={4}>
                  <Card
                    Icon={item?.icon}
                    stats={item?.stats}
                    title={item?.title}
                    isLoading={isLoading}
                  />
                </Grid>
              </React.Fragment>
            );
          })}
        </Grid>
      )}

      <Box sx={{ borderBottom: 1, borderColor: "divider", marginTop: "1rem" }}>
        <Tabs
          value={activeTab}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="scrollable auto tabs example"
          TabIndicatorProps={{
            style: {
              backgroundColor: "#9A469B",
              color: "red !important",
            },
          }}
          sx={{
            "& .MuiTab-root.Mui-selected": {
              color: "#9A469B",
            },
          }}
        >
          {tab_items?.map((item) => {
            return (
              <Tab
                key={item?.value}
                label={item?.label}
                className={styles.tabs}
                onClick={() => {
                  // Show the skeleton straight away on a real tab change: the
                  // refetch only runs in an effect, so the tab would otherwise
                  // paint "No Data Found" against the previous tab's data.
                  // Guarded, because re-clicking the active tab refetches
                  // nothing and would leave the loader stuck on.
                  if (item?.value !== activeTab) {
                    setLoader(true);
                  }
                  setActiveTab(item?.value);
                  setActivePage(1);
                  setSearch("");
                  setSearchText("");
                  setSearchParams({ tab: item?.slug });
                }}
              />
            );
          })}
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <Box className={styles.topLevelContainer}>
          <Box className={styles.topHeadContainer}>
            <Box>
              <div className={styles.tableHeading}>Newly registered users.</div>
              <div className={styles.tableSubHeading}>
                Manage your recently added users from here.
              </div>
            </Box>
            <Box className={styles.filterContainer}>
              <div className={styles.gridContainer}>
                <Icons.SearchIcon className={styles.searchIcon} />
                <input
                  placeholder="Search"
                  className={styles.input}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </Box>
          </Box>
          <TableContainer component={Paper} className={styles.table}>
            <Table aria-label="simple table">
              <TableHead>
                <TableRow className={styles.tableHead}>
                  <TableCell>Participant name</TableCell>
                  <TableCell>Roles</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Agreement Signed</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loader ? (
                  <SkeletonLoader />
                ) : (
                  users?.map((item: any, index: any) => {
                    return (
                      <TableRow key={item?.id}>
                        <TableCell>
                          <Box display={"flex"} alignItems={"center"}>
                            <Avatar
                              src={
                                item?.publisher
                                  ? item?.publisher?.profile_picture
                                  : item?.author?.profile_picture
                              }
                            />
                            <span
                              className={styles.tableItem}
                              style={{ marginLeft: "10px" }}
                            >
                              {item?.publisher
                                ? item?.publisher?.full_name
                                : item?.author?.full_name}{" "}
                            </span>
                          </Box>
                        </TableCell>{" "}
                        <TableCell>
                          {
                            admin_roles.find(
                              (role) => role.id === Number(item?.role)
                            )?.label
                          }
                        </TableCell>
                        <TableCell>{item?.email}</TableCell>
                        <TableCell>
                          {item?.latest_mou
                            ? MOU_Status.find(
                                (itm: any) =>
                                  itm.value === Number(item?.latest_mou?.status)
                              )?.label
                            : "No agreement yet."}
                        </TableCell>
                        <TableCell>
                          <Box
                            display={"flex"}
                            alignItems={"center"}
                            className={styles.actionBtn}
                          >
                            <Tooltip arrow title="View Detail">
                              <button
                                onClick={() =>
                                  navigate(
                                    Number(item?.role) === 6
                                      ? routeConstant.publisherProfile.path.replace(
                                          ":id",
                                          item?.id
                                        )
                                      : routeConstant.singleAuthor.path.replace(
                                          ":id",
                                          item?.id
                                        )
                                  )
                                }
                              >
                                <RemoveRedEyeIcon />
                              </button>
                            </Tooltip>
                            <Tooltip arrow title="Ticket">
                              <button
                                onClick={() =>
                                  navigate(
                                    routeConstant.singleTicket.path.replace(
                                      ":id",
                                      item?.id
                                    ),
                                    {
                                      // The ticket page reads its profile card
                                      // from navigation state, so the row has
                                      // to be handed over.
                                      state: {
                                        user: item,
                                      },
                                    }
                                  )
                                }
                              >
                                <ChatBubbleIcon />
                              </button>
                            </Tooltip>
                            <Tooltip arrow title="Agreement">
                              <button
                                onClick={() =>
                                  navigate(
                                    routeConstant.mou.path.replace(
                                      ":id",
                                      item?.id
                                    ),
                                    {
                                      state: { isAdmin: true },
                                    }
                                  )
                                }
                              >
                                <ArticleIcon />
                              </button>
                            </Tooltip>
                            {/* <Tooltip arrow title="Delete">
                              <button onClick={() => handleOpenDelete()}>
                                <DeleteIcon />
                              </button>
                            </Tooltip> */}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {!(users?.length > 0) && !loader && (
            <div className={styles.noUserCard}>
              <Icons.NoUsersData />
              <h5>No Data Found</h5>
            </div>
          )}
          {totalPage > 1 ? (
            <Pagination
              totalPages={totalPage}
              activePage={activePage}
              setActivePage={setActivePage}
            />
          ) : null}
          {users?.length > 0 ? (
            <ItemsPerPage
              setActivePage={setActivePage}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              totalItems={totalItems}
            ></ItemsPerPage>
          ) : null}
        </Box>
      )}

      {activeTab === 1 && <DemoRequests />}

      <Confirmation
        open={openDelete}
        handleClose={() => setOpenDelete(false)}
        handleClick={handleClick}
        text="Are you sure, you want to delete this publisher?"
      />
    </div>
  );
}

export default Dashboard;
